// Story 6.6 — runtime FPS guard for the reveal ceremony.
//
// The rAF plumbing that drives the pure `createFpsMonitor` decision logic
// and, when it trips, latches the session into the reduced mobile-
// fidelity tier via `degradeFidelityForSession()`.
//
// Mount this in whatever renders <RevealCeremony>, passing `active` for
// the span the ceremony is on screen (approach → settled). While active
// it samples every animation frame; the moment the monitor's verdict
// flips to degrade it fires the session switch and stops sampling (the
// verdict is latched — no reason to keep a rAF loop alive).
//
// It never degrades on its own re-render and reads/writes the session
// flag imperatively, so it adds no render-loop coupling.

import { useEffect } from "react";

import {
  createFpsMonitor,
  type FpsMonitorConfig,
} from "./fpsMonitor";
import {
  degradeFidelityForSession,
  isSessionFidelityDegraded,
} from "./sessionFidelityStore";

export function useCeremonyFpsGuard(
  active: boolean,
  config?: Partial<FpsMonitorConfig>,
): void {
  useEffect(() => {
    if (!active) return;
    // Already degraded for this session — nothing left to detect.
    if (isSessionFidelityDegraded()) return;
    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      return;
    }

    const monitor = createFpsMonitor(config);
    let rafId = 0;
    let stopped = false;

    const tick = (now: number) => {
      if (stopped) return;
      const verdict = monitor.pushFrame(now);
      if (verdict.degrade) {
        degradeFidelityForSession();
        return; // stop sampling — the verdict is latched
      }
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      stopped = true;
      window.cancelAnimationFrame(rafId);
    };
    // `config` is treated as stable per mount; callers pass a literal or
    // memoised object. Re-running on a fresh object each render would
    // restart the monitor and lose warm-up state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
